import { useResourcesBySource } from '@/api'
import { useAppSelector } from '@/app'
import { CALENDAR_LAST_HOUR } from '@/app/constants'
import {
  CalendarColumns,
  RouteHeaderContainer,
} from '@/features/calendar/components'
import { calendarModelMapper } from '@/features/calendar/domain'
import { useAutoscroll } from '@/features/calendar/util'
import { selectors as coreSelectors } from '@/features/core'
import { LinearProgress, Paper } from '@mui/material'
import dayjs from 'dayjs'
import { ServiceVisitList } from '../calendar/components/ServiceVisitList'
import { removeChildrenFilter } from '../calendar/domain/serviceVisitModel'
import { useStyles } from './EmployeeCalendar.style'

interface Props {
  id: string
  date: string
  scale?: number
  gutterInterval?: number
  legacyGrouping?: boolean
}
export const EmployeeCalendar = ({
  id,
  date,
  scale = 1,
  gutterInterval = 30,
  legacyGrouping = false,
}: Props) => {
  const { classes } = useStyles()
  const { hideInactiveVisits } = useAppSelector(coreSelectors.selectFlags)
  const { isLoading, data: visits } = useResourcesBySource(
    'ServiceVisit',
    'Employee',
    id,
    date,
    date,
    {
      select: data =>
        data
          .filter(it => it.data.state !== 'Deleted')
          .filter(it =>
            hideInactiveVisits
              ? !calendarModelMapper.mapServiceVisit(it).isInactive()
              : true
          )
          .filter(removeChildrenFilter),
      refetchOnWindowFocus: 'always',
      refetchInterval: 5000,
    }
  )

  // Staleness self-heal lives app-wide in `useVisitSelfHeal` (mounted in the
  // shell) so every calendar view — not just this one — recovers after a
  // freeze/resume under the mobile staleTime:Infinity default.

  const items = (visits ?? []).map(it => ({
    position: it.data.from,
    size: it.data.duration,
    item: it,
  }))
  const { data: dayRouteHeaders } = useResourcesBySource(
    'DayRouteHeader',
    'Employee',
    id,
    date,
    date
  )

  const { ref: scrollContainerRef } = useAutoscroll(date, items, scale)
  return (
    <>
      <LinearProgress
        variant={isLoading ? 'indeterminate' : 'determinate'}
        value={100}
      />
      <Paper elevation={3} className={classes.container}>
        <div ref={scrollContainerRef} className={classes.routeContainer}>
          <RouteHeaderContainer
            routes={
              dayRouteHeaders?.map(d => ({
                id: d.data.route,
                date: d.data.date ?? '',
              })) ?? []
            }
          />
          <CalendarColumns
            scale={scale}
            totalMinutes={CALENDAR_LAST_HOUR * 60}
            legacyGrouping={legacyGrouping}
            gutterInterval={gutterInterval}
            selectedDate={dayjs(date, 'YYYY-MM-DD')}
          >
            <ServiceVisitList
              visits={visits ?? []}
              scale={scale}
              legacyLayout={legacyGrouping}
            />
          </CalendarColumns>
        </div>
      </Paper>
    </>
  )
}
